import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Typography } from '@mui/material';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { HeaderMainNav, HeaderNavigationLinksItem } from '@/graphql/__generated/sdk';
import GroupNavDesktopMenu from './GroupNavDesktopMenu';
import { getFeaturedLinkStyles } from './GroupNavItem.styles';
import GroupNavMobileLink from './GroupNavMobileLink';
import GroupNavMobileMenu from './GroupNavMobileMenu';
import { itemIsFooterNavigationGroup, itemIsNavigationGroup, itemIsNavigationLink, itemIsPage } from '@components/Header/NavigationBar/NavigationBarItem/NavigationBarItem.helpers';
import { navItemWithIconStyles, navGroupLinkStyles, underlineStyles, getNavItemStyles, mobileNavItemStyles, navLinkHeight } from '@components/Header/NavigationBar/NavigationBarItem/NavigationBarItem.styles';
import NavigationLink from '@shared-components/components/NavigationLink/NavigationLink';
import TAGLink from '@shared-components/components/TAGLink';
import { incorrectUsageWarning } from '@utils/miscUtils';
export type GroupNavItemSource = HeaderMainNav | HeaderNavigationLinksItem;
export interface IGroupNavItem {
  item: GroupNavItemSource;
  itemIndex: number;
  isMobile?: boolean;
  toolbarRef?: React.RefObject<HTMLDivElement>;
  open?: boolean;
  onToggle?: () => void;
  onClose?: () => void;
}

// headerMainNavLink/headerSubNavMenu are optional, so plain `in` narrowing can't fully exclude HeaderMainNav afterwards; an explicit predicate can.
function itemIsHeaderMainNav(item: GroupNavItemSource): item is HeaderMainNav {
  return 'headerMainNavLink' in item;
}

// NavigationGroup already has the exact shape GroupNavDesktopMenu/GroupNavMobileMenu expect for a single sub-nav column (groupLink + linksCollection).
function resolveGroupNavItem(item: GroupNavItemSource) {
  if (itemIsHeaderMainNav(item)) {
    return {
      mainLink: item.headerMainNavLink,
      subNavGroups: item.headerSubNavMenu?.headerSubNavGroupsCollection?.items,
      contentTiles: item.headerSubNavMenu?.contentTilesCollection?.items,
      featuredLink: item.headerSubNavMenu?.featuredLink
    };
  }
  if (itemIsNavigationGroup(item)) {
    return {
      mainLink: item.groupLink,
      subNavGroups: [item],
      contentTiles: undefined,
      featuredLink: undefined
    };
  }
  if (itemIsNavigationLink(item)) {
    return {
      mainLink: item,
      subNavGroups: undefined,
      contentTiles: undefined,
      featuredLink: undefined
    };
  }
  if (itemIsPage(item) || itemIsFooterNavigationGroup(item)) {
    incorrectUsageWarning('GroupNavItem', `Unsupported Content Type (${item.__typename}) passed to GroupNavItem. GroupNavItem must be of type HeaderMainNav, NavigationGroup, or NavigationLink.`);
  }
  return {
    mainLink: undefined,
    subNavGroups: undefined,
    contentTiles: undefined,
    featuredLink: undefined
  };
}
export const FeaturedLinkRender = ({
  featuredLink,
  isMobile
}: {
  featuredLink: any;
  isMobile?: boolean;
}) => {
  return featuredLink?.linkText && <GroupNavMobileLink {...featuredLink} isInternal={Boolean(featuredLink.isInternal)} dataTestId={`link_navlink_nav_group_featured_${featuredLink.sys.id}`} trailingIcon linkSx={getFeaturedLinkStyles()} textVariant={isMobile ? 'header3' : 'header2'} />;
};
export default function GroupNavItem({
  item,
  itemIndex,
  isMobile,
  toolbarRef,
  open = false,
  onToggle,
  onClose
}: Readonly<IGroupNavItem>) {
  const router = useRouter();
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    onToggle?.();
  };
  const handleClose = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    onClose?.();
  };
  useEffect(() => {
    const closeMenu = () => onClose?.();
    globalThis.window.addEventListener('scroll', closeMenu);
    return () => {
      globalThis.window.removeEventListener('scroll', closeMenu);
    };
  }, [onClose]);
  useEffect(() => {
    const closeMenu = () => onClose?.();
    router.events.on('routeChangeComplete', closeMenu);
    return () => {
      router.events.off('routeChangeComplete', closeMenu);
    };
  }, [router.events, onClose]);
  const {
    mainLink,
    subNavGroups,
    contentTiles,
    featuredLink
  } = resolveGroupNavItem(item);
  if (!mainLink) {
    return null;
  }
  if (!subNavGroups?.length) {
    if (isMobile) {
      return <GroupNavMobileLink {...mainLink} isInternal={Boolean(mainLink.isInternal)} dataTestId={`link_page_nav_group_item_${mainLink.sys.id}`} analyticsContext={mainLink.analyticsContext} linkSx={{
        ...mobileNavItemStyles
      }} textVariant="bodyLargeSemiBold" textSx={{
        color: 'text.interactive'
      }} />;
    }
    return <NavigationLink {...mainLink} isInternal={Boolean(mainLink.isInternal)} itemIndex={itemIndex} dataTestId={`link_page_nav_item_${mainLink.sys.id}`} eventType="navigation" navLinkSx={{
      ...getNavItemStyles(),
      ...navLinkHeight
    }} />;
  }
  const groupMenuId = `header_navigation_group_menu_${itemIndex}`;
  return <>
			{/* Primary link that toggles the group's dropdown/drill-down menu */}
			{isMobile ? <GroupNavMobileLink {...mainLink} isInternal={Boolean(mainLink.isInternal)} dataTestId={`link_page_nav_group_item_${mainLink.sys.id}`} analyticsContext={mainLink.analyticsContext} onClick={handleClick} ariaControls={groupMenuId} ariaHasPopup className={open ? 'active' : ''} linkSx={{
      ...mobileNavItemStyles
    }} textVariant="bodyLargeSemiBold" chevronIcon /> : <TAGLink dataTestId={`link_page_nav_group_item_${mainLink.sys.id}`} onClick={handleClick} ariaControls={groupMenuId} ariaHasPopup analyticsContext={mainLink.analyticsContext} linkSx={{
      ...underlineStyles,
      ...navLinkHeight
    }} className={open ? 'active' : ''}>
					<Typography variant="bodyLargeSemiBold" color="text.primary" sx={[navGroupLinkStyles.groupLinkTitleStyles, navItemWithIconStyles]}>
						{mainLink.linkText}
						<Typography color="tertiary.main" sx={{
          display: 'inline'
        }}>
							<KeyboardArrowDownIcon sx={navGroupLinkStyles.getGroupLinkIconStyles(open)} />
						</Typography>
					</Typography>
				</TAGLink>}

			{!isMobile && <GroupNavDesktopMenu groupMenuId={groupMenuId} open={open} toolbarRef={toolbarRef} onClose={handleClose} contentTiles={contentTiles} subNavGroups={subNavGroups} featuredLink={featuredLink} />}

			{/* Submenu - mobile drill-down menu */}
			{isMobile && <GroupNavMobileMenu open={open} toolbarRef={toolbarRef} label={mainLink.linkText ?? undefined} onClose={handleClose} contentTiles={contentTiles} subNavGroups={subNavGroups} featuredLink={featuredLink} />}
		</>;
}